<template>
  <!-- prettier-ignore -->
  <div class="contract">
    <div class="contract__content">
      <span
        class="title-2"
        v-text="$t('basket.plz')"
      />
      <span
        class="body-2"
        v-text="location"
      />
      <basic-text-button
        v-if="showEditButton"
        class="contract__edit"
        :label="$t('form.edit')"
        @click="openEditContainer = true"
      >
        <template #trailing>
          <the-icon
            art="solid"
            name="pencil"
          />
        </template>
      </basic-text-button>
    </div>

    <basket-edit-container
      v-model:open="openEditContainer"
      class="one-line-grid"
      form-name="basket_contract"
      :title="$t('screen.basket.editTitle')"
      :v="v"
      @cancel="reset"
      @submit="update"
    >
      <basic-location
        v-model="formData.address"
        class="contract__location form__location"
        required
        :contract-start-date="basketStore.basket.contractStartDate"
        :label="$t('person.plz')"
        :v="v.address"
      />
    </basket-edit-container>
  </div>
</template>

<script setup>
  import { computed, reactive, ref, watch, watchEffect } from 'vue'
  import { useRouter } from 'vue-router'
  import useVuelidate from '@vuelidate/core'


  import basketStore from '@shared/store/basket'
  import useApplication from '@shared/hooks/useApplication'
  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import { BasicLocation, BasicTextButton } from '@shared/components/Basic'
  import BasketEditContainer from '@shared/components/Basket/Edit/Container'

  import validations from '@shared/validations/basketContract'
  import { ROUTE_NAME } from '@shared/config/constants'

  // HOOKS
  const { isWww } = useApplication()
  const router = !isWww ? useRouter() : {}

  // INIT
  const emit = defineEmits(['change-contract'])
  const props = defineProps({
    contractData: {
      type: Object,
      required: true,
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },

    isImmigrant: {
      type: Boolean,
      default: undefined,
    },

    isReadOnlyLocation: {
      type: Boolean,
      default: false,
    },

    openLocationInEditMode: {
      type: Boolean,
      default: false,
    },
  })

  // DATA
  const formData = reactive({
    address: {},
  })

  const openEditContainer = ref(false)

  // COMPUTED
  const canEdit = computed(() => {
    return props.isImmigrant === false && !props.isReadOnlyLocation
  })

  const location = computed(() => {
    return props.contractData?.address?.displayName
  })

  const showEditButton = computed(() => {
    return canEdit.value && !openEditContainer.value
  })

  // METHODS
  function initializeForm() {
    formData.address = ReactivityUtil.clone(props.contractData.address)
  }

  function reset() {
    initializeForm()

    if (props.openLocationInEditMode) {
      router.push({ name: ROUTE_NAME.BASKET })
    }
  }

  function update() {
    emit('change-contract', {
      data: formData,
      callback: () => {
        openEditContainer.value = false
        reset()
      },
    })
  }

  // VALIDATION
  const v = useVuelidate(validations, formData, { $lazy: true })

  // WATCHERS
  watchEffect(() => {
    initializeForm()
  })

  watch(
    () => props.openLocationInEditMode,
    () => {
      if (props.openLocationInEditMode) {
        openEditContainer.value = true
      }
    },
    { immediate: true }
  )
</script>

<style scoped>
.contract__content {
  display: flex;
  flex-direction: column;
}

.contract__edit {
  width: max-content;
}

@media (--v-medium) {
  .contract__content {
    flex-direction: row;
    gap: var(--fixed-spacing-fix-02);
  }

  .contract__edit {
    margin: 0 0 0 var(--fixed-spacing-fix-02);
  }
}

@media (--v-large) {
  .contract__location {
    height: 48px;
    width: 50%;

    input {
      margin: 0;
    }
  }
}
</style>
